import { useCallback, useEffect, useState } from 'react';
import {
  fetchApplicationFormDepartments,
  upsertApplicationFormDepartment,
} from '../../../lib/applicationFormWorkflow';
import { useFormsSettingsRealtime } from '../../../hooks/useFormsSettingsRealtime';
import type { ApplicationFormDepartment } from '../../../types/applicationForm';

type DepartmentsPanelProps = {
  /** Called after add / activate / deactivate so User management can refresh dropdowns. */
  onChanged?: () => void;
  /** When true, omit outer card chrome (for use inside a modal). */
  embedded?: boolean;
};

/** Shared department master list (User management + Forms workflow). */
export function ApplicationFormDepartmentsPanel({ onChanged, embedded = false }: DepartmentsPanelProps) {
  const [rows, setRows] = useState<ApplicationFormDepartment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ name: '', sort_order: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchApplicationFormDepartments(true));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load departments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSettingsRealtime({
    tables: ['departments'],
    onChange: () => {
      void load();
    },
  });

  function openCreate() {
    setError(null);
    setMessage(null);
    setDraft({ name: '', sort_order: (rows.length + 1) * 10 });
    setCreateOpen(true);
  }

  function closeCreate() {
    if (saving) return;
    setCreateOpen(false);
    setDraft({ name: '', sort_order: 0 });
  }

  async function addDepartment(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.name.trim()) {
      setError('Department name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await upsertApplicationFormDepartment({
        name: draft.name,
        sort_order: draft.sort_order,
      });
      setCreateOpen(false);
      setDraft({ name: '', sort_order: 0 });
      setMessage('Department saved.');
      await load();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save department.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row: ApplicationFormDepartment) {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await upsertApplicationFormDepartment({
        id: row.id,
        name: row.name,
        slug: row.slug,
        sort_order: row.sort_order,
        active: !row.active,
      });
      await load();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update department.');
    } finally {
      setSaving(false);
    }
  }

  const list = (
    <>
      {error ? <p className="alert alert-error">{error}</p> : null}
      {message ? <p className="alert alert-success">{message}</p> : null}

      {loading ? (
        <p className="page-lead">Loading departments…</p>
      ) : rows.length === 0 ? (
        <p className="settings-empty">No departments yet. Add one to begin.</p>
      ) : (
        <div className="settings-table-wrap">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Order</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>
                    <code>{row.slug}</code>
                  </td>
                  <td>{row.sort_order}</td>
                  <td>{row.active ? 'Active' : 'Inactive'}</td>
                  <td>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={saving}
                      onClick={() => void toggleActive(row)}
                    >
                      {row.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  const createForm = createOpen ? (
    <form className="settings-add-card unified-users-dept-create" onSubmit={(e) => void addDepartment(e)}>
      <h3 className="settings-add-card__title">Add department</h3>
      <div className="unified-users-dept-create__row">
        <label className="field">
          <span className="field-label">Name</span>
          <input
            className="field-input"
            value={draft.name}
            onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="e.g. Procurement"
            required
            autoFocus
          />
        </label>
        <label className="field unified-users-dept-create__order">
          <span className="field-label">Sort order</span>
          <input
            className="field-input"
            type="number"
            value={draft.sort_order}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, sort_order: Number(e.target.value) || 0 }))
            }
          />
        </label>
      </div>
      <div className="unified-users-create__footer">
        <button type="button" className="btn-secondary btn-sm" onClick={closeCreate} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="btn-primary btn-sm" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  ) : null;

  if (embedded) {
    return (
      <div className="unified-users-create">
        <div className="unified-users-panel__title-row">
          <p className="settings-panel__lead unified-users-dept-modal__lead">
            Assign people on each user card. IT and Management departments grant full system access.
          </p>
          {!createOpen ? (
            <button type="button" className="btn-primary btn-sm" onClick={openCreate}>
              Add department
            </button>
          ) : null}
        </div>
        {createForm}
        {list}
      </div>
    );
  }

  return (
    <section className="card settings-panel">
      <div className="settings-panel__head unified-users-panel__head">
        <div className="unified-users-panel__title-row">
          <h2 className="settings-panel__title">Departments</h2>
          {!createOpen ? (
            <button type="button" className="btn-primary btn-sm" onClick={openCreate}>
              Add department
            </button>
          ) : null}
        </div>
        <p className="settings-panel__lead">
          Master list for User management grouping and Application Form workflow.
        </p>
      </div>
      {createForm}
      {list}
    </section>
  );
}
